/**
 * Simplified Chatbot Store with Zustand
 * Single source of truth for chatbot state
 */

import { create } from 'zustand';
import { createJSONStorage, persist, subscribeWithSelector } from 'zustand/middleware';
import {
  ChatbotStore,
  ChatbotStoreState,
  ChatbotState,
  ChatbotView,
  MessageType,
  MessageStatus,
  ResponseStage,
  ChatbotMessage,
  ResponseProgress,
  ChatbotError,
  ChatbotSettings,
  ConversationHistoryItem,
  UploadedImage,
  ContinuationState,
  StreamInterruptedState,
  SSEConnectionState,
  InterruptAction,
  ExecutionTodo,
} from './types';
import posthog from '@libs/posthog';
import { EVENTS } from '@lib/analytics';
import chatbotApi from '@services/chatbot/chatbotApi';
import NewConversationService from '@apis/NewConversationService';
import { parseSuggestedResponses } from '@utils/parseSuggestedResponses';
import { toast } from 'react-toastify';
import { queryClient } from '../../libs/queryClient';
import { chatHistoryQueryKeys } from '@apis/queries/useChatHistory';
import { getAppStorePersistKey } from '@stores/utils';
import {
  setChatContext,
  clearChatContext,
  addChatBreadcrumb,
  traceChatTurn,
  recordFirstToken,
  recordStreamComplete,
  captureChatWarning,
  captureChatError,
} from '../../libs/sentryChat';
import { normalizeExecutionTodo } from '@services/chatbot/executionTodoState';

// ── Conversation tracking state for PostHog analytics ──────────────
let _conversationStartedAt: number | null = null;
let _conversationMessageCount = 0;

function _resetConversationTracking() {
  _conversationStartedAt = null;
  _conversationMessageCount = 0;
}

function _captureConversationEnded(conversationId: string | undefined) {
  if (!conversationId || !_conversationStartedAt) return;
  const durationMs = Date.now() - _conversationStartedAt;
  posthog.capture(EVENTS.CONVERSATION.ENDED, {
    conversation_id: conversationId,
    message_count: _conversationMessageCount,
    duration_seconds: Math.round(durationMs / 1000),
  });
  _resetConversationTracking();
}

// Default settings
const STORE_NAME = 'chatbot';

const defaultSettings: ChatbotSettings = {
  mockMode: false, // Use real API by default
};

// Maximum message length (50K chars = ~12.5K tokens)
// This prevents context overflow in Leo conversations
const MAX_MESSAGE_CHARS = 50000;

// Timeouts for retry vs resume decision (in milliseconds)
const SSE_SILENT_RETRY_TIMEOUT = 15000; // 15 seconds - show reconnect toast
const SSE_RECONNECT_TIMEOUT = 30000; // 30 seconds - show Resume banner
const SSE_WORKER_TIMEOUT = 3600000; // 1 hour - worker has timed out (resume still allowed)

let phaseUpdateTimeout: ReturnType<typeof setTimeout> | null = null;

// Initial continuation state
const initialContinuationState: ContinuationState = {
  isRecovering: false,
  originalConversationId: null,
  retryMessage: null,
  recoveryStartTime: null,
};

// Initial stream interrupted state
const initialStreamInterruptedState: StreamInterruptedState = {
  isInterrupted: false,
  partialResponse: '',
  error: '',
  isRetryable: true,
  originalMessage: '',
  uploadedImages: [],
  interruptedAt: null,
  conversationId: null,
  source: 'unknown',
  phase: 'idle',
  disconnectStartedAt: null,
  lastReconnectAttemptAt: null,
};

// Initial SSE connection state
const initialSSEConnectionState: SSEConnectionState = {
  isConnected: true,
  disconnectedAt: null,
  lastEventId: 0,
  reconnectAttempts: 0,
};

// Helper to normalize chatbot state on hydration (convert transient states to stable)
const normalizeChatbotState = (state: ChatbotState | undefined): ChatbotState => {
  if (!state || !Object.values(ChatbotState).includes(state)) {
    return ChatbotState.COLLAPSED; // Default to COLLAPSED if invalid or missing
  }
  // Convert transient states to stable states on page load
  if (state === ChatbotState.OPENING) {
    return ChatbotState.OPEN;
  }
  if (state === ChatbotState.CLOSING) {
    return ChatbotState.COLLAPSED;
  }
  return state;
};


// Initial state
// NOTE: Zustand persist middleware hydrates 'state' and 'conversationId' per workspace (key uses getAppStorePersistKey so each workspace has its own persisted state).
// The component must call initializeStore() on mount to load the conversation messages when conversationId is set.
const initialState: ChatbotStoreState = {
  // Core data
  messages: [],
  conversationId: undefined,

  // UI state
  state: ChatbotState.COLLAPSED, // Default closed; hydrated from persisted state for returning users
  responseStage: ResponseStage.IDLE,
  currentView: ChatbotView.CHAT,
  inputValue: '',
  inputFocusRequestId: 0,
  inputFocused: false,
  inputPlaceholderOverride: null,
  showWelcomeScreen: true, // Will be updated based on conversationId after hydration

  // Response tracking
  currentResponse: null,
  activeTools: [],

  // Message capacity tracking
  messageCapacity: null,
  executionTodo: null,

  // Continuation state for context overflow recovery
  continuation: initialContinuationState,

  // Stream interrupted state for retry/resume
  streamInterrupted: initialStreamInterruptedState,

  // SSE connection state
  sseConnection: initialSSEConnectionState,

  // Chat history
  conversationHistory: [],
  historyLoading: false,
  historySearchText: '',
  conversationLoading: false, // Will be updated based on conversationId after hydration

  // Error handling
  error: null,

  // Settings
  settings: defaultSettings,

  // Side modal state
  isAssetHubModalOpen: false,

  // Pending artifacts (queued from outside ChatbotContext, consumed on render)
  pendingArtifacts: [],
};

// Persist state and conversationId per workspace (getAppStorePersistKey includes workspace, so switching workspaces restores that workspace's conversation).
const chatbotStoreStorage = createJSONStorage<Pick<
  ChatbotStoreState,
  | 'state'
  | 'conversationId'
>>(() => ({
  getItem: (_name: string) => localStorage.getItem(getAppStorePersistKey(STORE_NAME)),
  setItem: (_name: string, value: string) => localStorage.setItem(getAppStorePersistKey(STORE_NAME), value),
  removeItem: (_name: string) => localStorage.removeItem(getAppStorePersistKey(STORE_NAME)),
}));


const clearInterruptionTimers = () => {
  if (phaseUpdateTimeout) {
    clearTimeout(phaseUpdateTimeout);
    phaseUpdateTimeout = null;
  }
};

const assertInterruptionState = (state: StreamInterruptedState, context: string) => {
  if (state.phase === 'blocked' && state.isRetryable) {
    console.warn('⚠️ [chatbotStore] Interrupted state mismatch: blocked but retryable', {
      context,
      phase: state.phase,
      isRetryable: state.isRetryable,
      error: state.error,
    });
  }
  if (state.phase === 'retry' && !state.isRetryable) {
    console.warn('⚠️ [chatbotStore] Interrupted state mismatch: retry but non-retriable', {
      context,
      phase: state.phase,
      isRetryable: state.isRetryable,
      error: state.error,
    });
  }
};

const normalizeUploadedImages = (images?: UploadedImage[]): UploadedImage[] =>
  (images || []).map((image) => ({
    ...image,
    name: image.name || image.file?.name,
    file: null,
  }));

const deriveInterruptAction = (streamInterrupted: StreamInterruptedState): InterruptAction => {
  if (!streamInterrupted.isInterrupted) return 'none';
  if (streamInterrupted.source === 'network' && streamInterrupted.phase === 'resume') {
    return 'resume';
  }
  if (streamInterrupted.isRetryable) {
    return 'retry';
  }
  return 'none';
};

const getNetworkPhaseForElapsed = (elapsedMs: number): StreamInterruptedState['phase'] => {
  if (elapsedMs >= SSE_RECONNECT_TIMEOUT) {
    return 'resume';
  }
  if (elapsedMs >= SSE_SILENT_RETRY_TIMEOUT) {
    return 'toast';
  }
  return 'silent';
};

const scheduleNetworkPhaseUpdate = (
  get: () => ChatbotStore,
  set: (partial: Partial<ChatbotStoreState>) => void,
  disconnectStartedAt: number
) => {
  const tick = () => {
    const currentState = get();
    const { sseConnection, streamInterrupted } = currentState;

    if (sseConnection.isConnected || streamInterrupted.source !== 'network') {
      return;
    }
    if (streamInterrupted.disconnectStartedAt !== disconnectStartedAt) {
      return;
    }

    const elapsedMs = Date.now() - disconnectStartedAt;
    const nextPhase = getNetworkPhaseForElapsed(elapsedMs);

    if (nextPhase === 'toast' && streamInterrupted.phase !== 'toast') {
      toast.info('Reconnecting…', {
        autoClose: 5000,
        toastId: 'connection-lost',
      });
      set({
        streamInterrupted: {
          ...streamInterrupted,
          phase: 'toast',
        },
      });
    }

    if (nextPhase === 'resume' && streamInterrupted.phase !== 'resume') {
      const isWorkerTimeout = elapsedMs >= SSE_WORKER_TIMEOUT;
      const resumeError = isWorkerTimeout
        ? 'Connection lost - worker may have timed out'
        : 'Connection lost - network issue';

      console.log(`⚠️ [chatbotStore] ${isWorkerTimeout ? 'Worker' : 'Network'} timeout - showing Resume`);
      toast.warning('Connection lost. Click Resume to reconnect.', {
        autoClose: false,
        toastId: 'connection-lost',
      });

      set({
        streamInterrupted: {
          ...streamInterrupted,
          isInterrupted: true,
          error: resumeError,
          phase: 'resume',
          interruptedAt: disconnectStartedAt,
        },
        responseStage: ResponseStage.IDLE,
        currentResponse: null,
      });
      return;
    }

    if (nextPhase === 'resume') {
      return;
    }

    if (nextPhase === 'silent' && streamInterrupted.phase !== 'silent') {
      set({
        streamInterrupted: {
          ...streamInterrupted,
          phase: 'silent',
        },
      });
    }

    const nextBoundary =
      nextPhase === 'silent' ? SSE_SILENT_RETRY_TIMEOUT : SSE_RECONNECT_TIMEOUT;
    const delayMs = Math.max(0, nextBoundary - elapsedMs);
    phaseUpdateTimeout = setTimeout(tick, delayMs);
  };

  clearInterruptionTimers();
  phaseUpdateTimeout = setTimeout(tick, 0);
};

// Utility functions
const generateId = () => `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

/** Derive a conversation title from the first user message */
const deriveConversationTitle = (messages: ChatbotMessage[]): string => {
  const firstUserMessage = messages.find(msg => msg.type === MessageType.USER);
  if (!firstUserMessage || typeof firstUserMessage.content !== 'string') return 'New Conversation';
  const content = firstUserMessage.content;
  return content.length > 50 ? content.substring(0, 50) + '...' : content;
};

const createMessage = (
  type: MessageType,
  content: string | any,
  status: MessageStatus = MessageStatus.SENT,
  images?: UploadedImage[],
  hidden?: boolean,
  display?: string
): ChatbotMessage => ({
  id: generateId(),
  type,
  content,
  timestamp: Date.now(),
  status,
  images,
  hidden,
  display,
});

// Capacity is now calculated by the backend and stored in S3 metadata
// Frontend only displays capacity, never calculates it
// Old conversations without metadata.capacity will show 0% until first message exchange

export const useChatbotStore = create<ChatbotStore>()(
  persist(
    subscribeWithSelector((set, get) => ({
      ...initialState,

      // Lifecycle actions
      openChatbot: (options?) => {
        const { initialMessage, hideInitialMessage, displayText, prefillInput, prefillArtifacts } = options || {};
        const hasAutoMessage = !!initialMessage;
        const hasPrefill = !!prefillInput || (prefillArtifacts && prefillArtifacts.length > 0);

        // Prevent double-click for auto-send mode
        if (hasAutoMessage && get().responseStage !== ResponseStage.IDLE) {
          return;
        }

        // Clear conversation for auto-send or prefill modes
        if (hasAutoMessage || hasPrefill) {
          get().clearConversation();
        }

        set({
          state: ChatbotState.OPENING,
          // Auto-send: immediately signal WAITING so the UI shows a loading
          // indicator and the guard above blocks subsequent calls.
          ...(hasAutoMessage ? {
            responseStage: ResponseStage.WAITING,
            showWelcomeScreen: false,
          } : {}),
          // Prefill: set input text and hide welcome screen
          ...(hasPrefill ? { inputValue: prefillInput || '', showWelcomeScreen: false } : {}),
        });

        setTimeout(() => {
          set({
            state: ChatbotState.OPEN,
            ...(hasPrefill ? { pendingArtifacts: prefillArtifacts || [] } : {}),
          });

          if (hasAutoMessage) {
            setTimeout(() => {
              get().sendMessage(initialMessage, undefined, hideInitialMessage, displayText);
            }, 300);
          }

          if (hasPrefill) {
            get().requestInputFocus();
          }
        }, 300);
      },

      collapseChat: () => {
        set({ state: ChatbotState.COLLAPSED });
      },

      // Messaging actions
      sendMessage: async (content: string, images?: UploadedImage[], hidden?: boolean, display?: string) => {
        if (!content.trim() && (!images || images.length === 0)) return;

        addChatBreadcrumb('send_message', {
          hasImages: Boolean(images?.length),
          hidden: Boolean(hidden),
          messageLength: content.length,
        });

        // Validate message length to prevent context overflow
        if (content.length > MAX_MESSAGE_CHARS) {
          set({
            error: {
              id: generateId(),
              code: 'MESSAGE_TOO_LONG',
              message: `Message too long (${content.length.toLocaleString()} characters). Maximum is ${MAX_MESSAGE_CHARS.toLocaleString()} characters.`,
              timestamp: Date.now(),
              retryable: false,
            }
          });
          return;
        }

        const { settings, addMessage, setResponseStage, currentResponse, conversationId } = get();

        // Track message sent
        _conversationMessageCount++;
        posthog.capture(EVENTS.CONVERSATION.MESSAGE_SENT, {
          conversation_id: conversationId ?? null,
          message_length: content.trim().length,
          has_images: Boolean(images?.length),
        });

        // Block if already processing (but allow if response is idle)
        if (currentResponse && currentResponse.stage !== ResponseStage.IDLE) return;

        // Clear previous response state when starting new message
        set({ currentResponse: null, responseStage: ResponseStage.IDLE });

        // Set response stage immediately to WAITING state
        setResponseStage(ResponseStage.WAITING, 'Leo is thinking...');

        // Add user message immediately with pre-uploaded images
        const messageContent = content.trim() || (images && images.length > 0 ? 'Images uploaded' : '');

        addMessage({
          type: MessageType.USER,
          content: messageContent,
          status: MessageStatus.SENT,
          images: images, // Use pre-uploaded images directly
          hidden: hidden, // Pass the hidden flag to the message
          display, // Optional display text shown in UI instead of content
        });

        // Clear input, error, and any stream interrupted state immediately
        // If user sends a new message while retry banner is showing, they're choosing to proceed
        clearInterruptionTimers();
        set({
          inputValue: '',
          error: null,
          showWelcomeScreen: false,
          streamInterrupted: initialStreamInterruptedState,
        });

        // Images are already uploaded, so we can proceed directly to sending the message
        // Start response progress tracking
        const requestId = generateId();

        try {
          if (settings.mockMode) {
            await get()._handleMockResponse(messageContent, requestId);
          } else {
            await get()._handleRealResponse(messageContent, requestId, conversationId, images, false, hidden, display);
          }
        } catch (error) {
          // Log technical details for debugging
          console.error('Send message error:', error);
          
          // Check for specific error codes
          const errorCode = (error as any)?.code;
          const isSubscriptionError = errorCode === 'SUBSCRIPTION_REQUIRED';
          
          // If subscription error, dispatch event to show subscription modal
          if (isSubscriptionError) {
            console.log('🔔 [chatbotStore] Subscription required in sendMessage - dispatching event');
            window.dispatchEvent(new CustomEvent('subscription-required'));
            
            // Don't show error in chat - the modal will handle it
            get().setResponseStage(ResponseStage.IDLE, '');
            setTimeout(() => {
              set({ currentResponse: null, responseStage: ResponseStage.IDLE });
            }, 100);
            return;
          }

          // For non-subscription errors, show calm stream interrupted UI
          const rawMessage = error instanceof Error ? error.message : '';
          const userFriendlyMessage = rawMessage || 'Having trouble connecting. Please try again in a moment.';

          get().handleStreamInterrupted(
            '',
            userFriendlyMessage,
            true,
            messageContent,
            images
          );
        }
      },

      retryMessage: async (messageId: string) => {
        const { messages } = get();
        const message = messages.find(m => m.id === messageId);

        if (message && message.type === MessageType.USER) {
          // Update message status
          get().updateMessage(messageId, {
            status: MessageStatus.RETRYING,
            retryCount: (message.retryCount || 0) + 1
          });

          // Retry sending (only if content is a string)
          if (typeof message.content === 'string') {
            await get().sendMessage(message.content);
          }
        }
      },

      cancelCurrentResponse: () => {
        const { conversationId } = get();
        // Signal worker to stop cooperatively (no-op when streaming direct)
        if (conversationId) {
          NewConversationService.cancelStream(conversationId).catch(() => {});
        }
        // Abort the network request so we stop reading the stream.
        // Unlike detachFromStream, we keep messages visible so the user
        // can see the partial response they chose to stop.
        chatbotApi.cancelRequest();

        // Best-effort: finalize any in-progress tool markers on the last assistant message
        // so tool spinners don't get stuck after a client-side abort.
        try {
          const { messages } = get();
          const lastAssistantIndex = [...messages].reverse().findIndex(m => m.type === MessageType.ASSISTANT);
          if (lastAssistantIndex !== -1) {
            const idx = messages.length - 1 - lastAssistantIndex;
            const lastAssistant = messages[idx];
            if (lastAssistant?.toolMarkers?.length) {
              const finalized = lastAssistant.toolMarkers.map(marker => ({
                ...marker,
                success: marker.success === null || marker.success === undefined ? false : marker.success,
              }));
              const updated = [...messages];
              updated[idx] = { ...lastAssistant, toolMarkers: finalized };
              set({ messages: updated });
            }
          }
        } catch (e) {
          console.warn('⚠️ [chatbotStore] Failed to finalize tool markers on cancel', e);
        }

        // Reset state to idle and clear any interruption/error UI so Stop stays quiet.
        set({
          currentResponse: null,
          responseStage: ResponseStage.IDLE,
          streamInterrupted: initialStreamInterruptedState,
          error: null,
        });
      },

      // Retry from error without adding duplicate user message
      retryFromError: async () => {
        const { error, conversationId, setResponseStage } = get();

        if (!error?.originalRequest || typeof error.originalRequest !== 'string') {
          console.warn('❌ [chatbotStore] No original request to retry');
          return;
        }

        const messageContent = error.originalRequest;

        // Clear error and set loading state
        set({ error: null });
        setResponseStage(ResponseStage.WAITING, 'Leo is thinking...');

        try {
          // Call _handleRealResponse directly without adding a new user message
          // Pass isRetry=true to tell backend not to save duplicate user message
          await get()._handleRealResponse(messageContent, generateId(), conversationId, undefined, true);
        } catch (retryError) {
          console.error('❌ [chatbotStore] Retry failed:', retryError);
          // Error handling is done inside _handleRealResponse
        }
      },

      clearConversation: () => {
        // Capture conversation ended before clearing
        _captureConversationEnded(get().conversationId);
        // Detach from any in-flight stream, then fully reset the
        // conversation identity so the UI shows the welcome screen.
        get().detachFromStream();
        clearChatContext();
        addChatBreadcrumb('clear_conversation');
        set({
          conversationId: undefined,
          showWelcomeScreen: true,
          conversationLoading: false,
          inputValue: '',
          currentView: ChatbotView.CHAT,
          messageCapacity: null,
          executionTodo: null,
        });
      },

      // ── Stream lifecycle ──────────────────────────────────────────
      //
      //   detachFromStream       – close HTTP, clear messages/transient state,
      //                            keep conversationId (workspace switch)
      //   clearConversation      – detach + forget conversation (new chat)
      //   cancelCurrentResponse  – signal worker + close HTTP, keep messages
      //                            visible (stop button)
      //
      detachFromStream: () => {
        // Disconnect the frontend from an in-flight SSE stream WITHOUT
        // cancelling the backend worker. The worker will finish execution
        // and save the complete response to S3, so the conversation is
        // available in history when the user returns to this workspace.
        chatbotApi.cancelRequest();
        clearInterruptionTimers();
        set({
          messages: [],
          currentResponse: null,
          responseStage: ResponseStage.IDLE,
          error: null,
          executionTodo: null,
          streamInterrupted: initialStreamInterruptedState,
          sseConnection: initialSSEConnectionState,
          conversationLoading: true,
          showWelcomeScreen: false,
        });
      },

      // Input handling
      setInputValue: (value: string) => {
        set({ inputValue: value });
      },
      setInputFocused: (value: boolean) => {
        set({ inputFocused: value });
      },
      setInputPlaceholderOverride: (value: string | null) => {
        set({ inputPlaceholderOverride: value });
      },
      requestInputFocus: () => {
        // Bump a monotonically increasing value so components can react and focus.
        set((state) => ({ inputFocusRequestId: state.inputFocusRequestId + 1 }));
      },
      clearPendingArtifacts: () => {
        if (get().pendingArtifacts.length > 0) {
          set({ pendingArtifacts: [] });
        }
      },

      // Progress tracking
      updateResponseProgress: (progress: Partial<ResponseProgress>) => {
        set(state => {
          const updates: any = {
            currentResponse: state.currentResponse
              ? { ...state.currentResponse, ...progress }
              : null
          };

          // If progress includes messageCapacity, also update root-level messageCapacity
          if (progress.messageCapacity) {
            updates.messageCapacity = progress.messageCapacity;
          }
          if (progress.executionTodo !== undefined) {
            updates.executionTodo = progress.executionTodo;
          }

          return updates;
        });
      },

      setResponseStage: (stage: ResponseStage, activity?: string) => {
        const now = Date.now();

        set(state => ({
          responseStage: stage,
          currentResponse: {
            requestId: state.currentResponse?.requestId || generateId(),
            stage,
            progress: getProgressForStage(stage),
            startTime: state.currentResponse?.startTime || now,
            estimatedDuration: 15000, // 15 seconds default
            currentActivity: activity || getDefaultActivityForStage(stage),
            canCancel: stage !== ResponseStage.IDLE && stage !== ResponseStage.ERROR,
          }
        }));
      },

      setActiveTools: (tools: string[]) => {
        set({ activeTools: tools });
      },

      setExecutionTodo: (todo: ExecutionTodo | null) => {
        set({ executionTodo: todo });
      },

      // Error handling
      setError: (error: ChatbotError | null) => {
        get().setResponseStage(ResponseStage.ERROR, 'Error occurred');
        set({ error: error });
      },

      clearError: () => {
        set({ error: null });
      },

      // Settings
      updateSettings: (newSettings: Partial<ChatbotSettings>) => {
        set(state => ({
          settings: { ...state.settings, ...newSettings }
        }));
      },

      toggleMockMode: () => {
        set(state => ({
          settings: {
            ...state.settings,
            mockMode: !state.settings.mockMode
          }
        }));
      },

      // Conversation management
      setConversationId: (id: string | undefined) => {
        const currentId = get().conversationId;

        // If switching to a different conversation (or clearing), reset capacity
        // Capacity will be recalculated when loadConversationById is called
        if (currentId !== id) {
          set({
            conversationId: id,
            messageCapacity: null, // Reset capacity when switching conversations
            executionTodo: null,
          });
        } else {
          set({ conversationId: id });
        }

      },

      loadConversationFromStorage: () => {
        // This method is now redundant since Zustand persist handles hydration
        // Keeping for backwards compatibility but it's a no-op
        const { conversationId } = get();
        if (conversationId) {
          // Already loaded via persist middleware
          return;
        }
      },

      // Initialize store on app mount - loads saved conversation if one exists
      // This MUST be called by the chatbot component on mount to load conversation history
      initializeStore: async () => {
        const { conversationId, messages } = get();

        // If we have a saved conversation ID but no messages loaded, load them
        if (conversationId && messages.length === 0) {
          console.log('🚀 [chatbotStore] Initializing - loading saved conversation:', conversationId);
          try {
            await get().loadConversationById(conversationId);
            console.log('✅ [chatbotStore] Saved conversation loaded successfully');
          } catch (error) {
            console.error('❌ [chatbotStore] Failed to load saved conversation:', error);
            // Clear the invalid conversation ID and show welcome screen
            set({
              conversationId: undefined,
              showWelcomeScreen: true,
              conversationLoading: false,
            });
          }
        } else if (!conversationId) {
          // No saved conversation - make sure loading state is cleared
          set({ conversationLoading: false });
        }
      },

      // Chat history management
      setCurrentView: (view: ChatbotView) => {
        set({ currentView: view });
      },

      loadConversationHistory: async () => {
        set({ historyLoading: true });
        try {
          const conversations = await NewConversationService.getAll();
          const historyItems: ConversationHistoryItem[] = conversations.map(conv => ({
            conversation_id: conv.conversation_id,
            title: conv.title,
            timestamp: conv.timestamp,
            last_activity_at: conv.last_activity_at,
          }));

          set({
            conversationHistory: historyItems,
            historyLoading: false
          });
        } catch (error) {
          console.error('Failed to load conversation history:', error);
          set({
            historyLoading: false,
            error: {
              id: generateId(),
              code: 'HISTORY_LOAD_ERROR',
              message: 'Failed to load conversation history',
              timestamp: Date.now(),
              retryable: true,
            }
          });
        }
      },

      saveCurrentConversation: async () => {
      const { messages, conversationId } = get();

      if (!messages.length || !conversationId) return;

      try {
        const nowEpochSeconds = Math.floor(Date.now() / 1000);
        const historyItem: ConversationHistoryItem = {
          conversation_id: conversationId,
          title: deriveConversationTitle(messages),
          timestamp: nowEpochSeconds,
          last_activity_at: nowEpochSeconds,
        };

        set(state => ({
          conversationHistory: [historyItem, ...state.conversationHistory.filter(
            item => item.conversation_id !== conversationId
          )]
        }));
      } catch (error) {
        console.error('Failed to save conversation:', error);
      }
    },

    deleteConversation: async (conversationId: string) => {
      try {
        // Delete from backend
        await NewConversationService.delete(conversationId);

        // Remove from local history
        set(state => ({
          conversationHistory: state.conversationHistory.filter(
            item => item.conversation_id !== conversationId
          )
        }));

        // If the deleted conversation is the current one, clear it
        const { conversationId: currentId } = get();
        if (currentId === conversationId) {
          get().clearConversation();
        }
      } catch (error) {
        console.error('Failed to delete conversation:', error);
        get().setError({
          id: generateId(),
          code: 'DELETE_ERROR',
          message: 'Failed to delete conversation. Please try again.',
          timestamp: Date.now(),
          retryable: true,
        });
        throw error;
      }
    },

    // Internal actions
    addMessage: (messageData) => {
      const message = createMessage(
        messageData.type,
        messageData.content,
        messageData.status,
        messageData.images,
        messageData.hidden,
        messageData.display
      );

      set(state => {
        const newMessages = [...state.messages, message];
        // Don't recalculate capacity here - use backend-provided capacity
        // Capacity is updated via capacity_info event from backend

        return {
          messages: newMessages,
          showWelcomeScreen: false, // Hide welcome screen when message is added
          // Keep existing messageCapacity from backend
        };
      });
    },

    updateMessage: (id: string, updates: Partial<ChatbotMessage>) => {
      set(state => {
        const updatedMessages = state.messages.map(msg =>
          msg.id === id ? { ...msg, ...updates } : msg
        );
        
        // Don't recalculate capacity here - use backend-provided capacity
        // Capacity is updated via capacity_info event from backend
        
        return {
          messages: updatedMessages,
          // Keep existing messageCapacity from backend
        };
      });
    },

    setState: (newState: ChatbotState) => {
      // State is automatically persisted via Zustand's persist middleware (workspace-aware)
      set({ state: newState });
    },

    // Internal helper methods
    _handleMockResponse: async (content: string, _requestId: string) => {
      const { setResponseStage, addMessage } = get();

      // Simple delay
      await new Promise(resolve => setTimeout(resolve, 3000));

      // Add mock response (can be string or structured content)
      const mockResponse = getMockResponse(content);
      addMessage({
        type: MessageType.ASSISTANT,
        content: mockResponse,
        status: MessageStatus.SENT,
      });

      // Complete - set stage to IDLE
      setResponseStage(ResponseStage.IDLE, 'Complete');

      // Clear currentResponse after a short delay to ensure UI updates
      // This allows the final message to render before clearing the response state
      setTimeout(() => {
        set({ currentResponse: null, responseStage: ResponseStage.IDLE });
      }, 100);
    },

    _handleRealResponse: async (content: string, _requestId: string, conversationId?: string, uploadedImages?: any[], isRetry?: boolean, isHidden?: boolean, displayText?: string) => {
      const { updateResponseProgress, addMessage, setConversationId, setResponseStage } = get();

      setChatContext({ conversationId: conversationId ?? 'new' });

      // Generate conversationId for new conversations (format: conv_{timestamp}_{uuid})
      // IMPORTANT: We set conversationId in state immediately but DON'T persist to localStorage yet.
      // This prevents saving an ID that may never be created on the server if the request fails.
      // We persist to localStorage only after successful response (see below).
      let activeConversationId = conversationId;
      const isNewConversation = !activeConversationId;
      if (!activeConversationId) {
        const timestamp = Math.floor(Date.now() / 1000);
        const uuid = Math.random().toString(36).substring(2, 10);
        activeConversationId = `conv_${timestamp}_${uuid}`;
        // Set in state only (don't persist to localStorage yet)
        set({ conversationId: activeConversationId });
      }

      // Helper to check if an error is retryable (network/connection issues only)
      const isRetryableError = (error: unknown): boolean => {
        if (!(error instanceof Error)) return false;
        const msg = error.message.toLowerCase();
        // Common error messages when network is unavailable
        return (
          msg.includes('network') ||
          msg.includes('timeout') ||
          msg.includes('err_http2') ||
          msg.includes('connection') ||
          msg.includes('fetch') ||
          msg.includes('aborted') ||
          msg.includes('failed to fetch') ||
          msg.includes('networkerror') ||
          msg.includes('net::') ||
          msg.includes('load failed') ||
          msg.includes('internet') ||
          msg.includes('offline')
        );
      };

      // Capture the conversation ID at stream-start time so callbacks
      // can detect if the user switched conversations/workspaces mid-stream.
      const streamConversationId = activeConversationId!;

      const isStaleStream = (): boolean => {
        const current = get().conversationId;
        return current !== streamConversationId;
      };

      try {
        let response: Awaited<ReturnType<typeof chatbotApi.sendMessage>> | undefined;

        // Always use streaming (now that we generate conversationId for new conversations)
        let streamedText = '';
        let streamingMessageId: string | null = null;
        const activeToolsSet = new Set<string>();

        const toolsUsedInMessage: string[] = [];
        const toolMarkers: Array<{ position: number; tool: string; runId?: string; message?: string; success?: boolean | null }> = [];

        // Throttle message updates to prevent React error 185 (max update depth)
        // During fast streaming, we only update the messages array every 100ms
        const throttleState = {
          lastUpdateTime: 0,
          pendingUpdate: null as (() => void) | null,
        };
        
        // STRESS TEST: Set to 0 to disable throttling and trigger error 185
        // Add ?stressTest=true to URL to test
        const isStressTest = typeof window !== 'undefined' && 
          new URLSearchParams(window.location.search).get('stressTest') === 'true';
        const MESSAGE_UPDATE_THROTTLE_MS = isStressTest ? 0 : 100;
        
        if (isStressTest) {
          console.warn('⚠️ STRESS TEST MODE: Throttling disabled - expect React error 185!');
        }

        const throttledMessageUpdate = (updateFn: () => void) => {
          const now = Date.now();
          if (now - throttleState.lastUpdateTime >= MESSAGE_UPDATE_THROTTLE_MS) {
            // Enough time has passed, update immediately
            throttleState.lastUpdateTime = now;
            updateFn();
          } else {
            // Store the pending update - it will be applied on next throttle window
            throttleState.pendingUpdate = updateFn;
          }
        };

        // Helper function to perform the streaming request
        const performStreamRequest = async (): Promise<Awaited<ReturnType<typeof chatbotApi.sendMessage>>> => {
          return await chatbotApi.sendMessageStream(
            content,
            activeConversationId!,
            uploadedImages,
            isRetry,
            undefined,
            undefined,
            isHidden,
            (progress) => {
              // GUARD: If the user navigated away (workspace switch, new chat, etc.)
              // ignore all further callbacks so stale data never touches the store.
              if (isStaleStream()) return;

              // Worker death recovery: when the backend sends response_reset, the
              // chatbotApi layer sets isStreamReset=true.  We must clear ALL local
              // streaming state here so the recovery stream starts clean.  Without
              // this, stale toolMarkers, streamedText, and streamingMessageId from
              // the dead worker's partial output persist and mix with the new
              // attempt's content (split-brain state).
              if (progress.isStreamReset) {
                toolMarkers.length = 0;
                streamedText = '';
                streamingMessageId = '';
                activeToolsSet.clear();
                toolsUsedInMessage.length = 0;
                get().setActiveTools([]);
                updateResponseProgress(progress);
                return;
              }

              // Track tool markers at their position in text
              // Update toolMarkers array to match the progress (preserve messages if already set)
              if (progress.toolMarkers && Array.isArray(progress.toolMarkers)) {
                // Merge progress markers with existing markers, preserving messages and success status
                progress.toolMarkers.forEach((progressMarker: { position: number; tool: string; runId?: string; message?: string; success?: boolean | null }) => {
                  // Use runId for matching if available, otherwise fall back to position+tool
                  const existingIndex = progressMarker.runId
                    ? toolMarkers.findIndex(m => m.runId === progressMarker.runId)
                    : toolMarkers.findIndex(m => m.position === progressMarker.position && m.tool === progressMarker.tool);

                  if (existingIndex !== -1) {
                    // Update existing marker, preserve message and success if progress marker has them
                    if (progressMarker.message !== undefined) {
                      toolMarkers[existingIndex] = {
                        ...toolMarkers[existingIndex],
                        runId: progressMarker.runId || toolMarkers[existingIndex].runId, // Preserve or update runId
                        message: progressMarker.message,
                        success: progressMarker.success !== undefined ? progressMarker.success : toolMarkers[existingIndex].success
                      };
                    } else if (progressMarker.success !== undefined) {
                      toolMarkers[existingIndex] = {
                        ...toolMarkers[existingIndex],
                        success: progressMarker.success
                      };
                    }
                  } else {
                    // Add new marker
                    toolMarkers.push(progressMarker);
                  }
                });
              }

              // Track tools - update activeToolsSet from progress
              if (progress.activeTools && Array.isArray(progress.activeTools)) {
                progress.activeTools.forEach((tool: string) => {
                  activeToolsSet.add(tool);
                  if (!toolsUsedInMessage.includes(tool)) {
                    toolsUsedInMessage.push(tool);
                  }
                });
                get().setActiveTools(Array.from(activeToolsSet));
              }

              // Update message capacity if provided
              if (progress.messageCapacity) {
                set({ messageCapacity: progress.messageCapacity });
              }

              // Update message content as it streams
              if (progress.streamedContent) {
                const newText = progress.streamedContent;
                if (newText !== streamedText) {
                  streamedText = newText;

                  // Set stage to STREAMING when content starts streaming
                  if (streamedText.length > 0 && get().currentResponse?.stage === ResponseStage.WAITING) {
                    setResponseStage(ResponseStage.STREAMING, 'Streaming response...');
                    recordFirstToken(chatSpanRef);
                  }

                  // Parse suggested responses from content (frontend parsing - synchronous)
                  const { content: cleanedContent, suggestions } = parseSuggestedResponses(streamedText);

                  // Update progress with tool markers
                  updateResponseProgress({
                    ...progress,
                    stage: ResponseStage.STREAMING, // Ensure stage is STREAMING during streaming
                    toolMarkers: toolMarkers,
                    streamedContent: cleanedContent, // Use cleaned content for display
                  });

                  // Add or update streaming message
                  if (!streamingMessageId) {
                    streamingMessageId = generateId();
                    addMessage({
                      type: MessageType.ASSISTANT,
                      content: cleanedContent, // Store cleaned content
                      status: MessageStatus.SENT,
                      toolMarkers: toolMarkers.length > 0 ? [...toolMarkers] : undefined,
                      suggestedResponses: suggestions.length > 0 ? suggestions : undefined,
                    });
                    // Update the message we just created with the ID
                    const { messages } = get();
                    const lastMessage = messages[messages.length - 1];
                    if (lastMessage) {
                      streamingMessageId = lastMessage.id;
                    }
                  } else {
                    // Update existing message (throttled to prevent React error 185)
                    throttledMessageUpdate(() => {
                      const { messages } = get();
                      const messageIndex = messages.findIndex(m => m.id === streamingMessageId);
                      if (messageIndex !== -1) {
                        const updated = [...messages];
                        updated[messageIndex] = {
                          ...updated[messageIndex],
                          content: cleanedContent, // Use cleaned content
                          toolMarkers: toolMarkers.length > 0 ? [...toolMarkers] : updated[messageIndex].toolMarkers,
                          suggestedResponses: suggestions.length > 0 ? suggestions : updated[messageIndex].suggestedResponses,
                        };
                        set({ messages: updated });
                        
                        // STRESS TEST: Force extra updates to trigger error 185
                        if (isStressTest) {
                          for (let i = 0; i < 5; i++) {
                            set({ messages: [...get().messages] });
                          }
                        }
                      }
                    });
                  }
                }
              }
            },
            displayText
          );
        };

        // Sentry span reference for chat turn tracing
        let chatSpanRef: import('@sentry/react').Span | undefined;

        // Auto-retry logic: 1 retry for transient network errors
        // User's message is saved on backend before streaming, so retry is safe
        let lastError: unknown = null;

        const executeStreamWithRetry = async (span: import('@sentry/react').Span | undefined) => {
          chatSpanRef = span;
          for (let attempt = 1; attempt <= 2; attempt++) {
          try {
            response = await performStreamRequest();
            break; // Success - exit retry loop
          } catch (error) {
            lastError = error;
            
            // Log the actual error for debugging
            const errorMsg = error instanceof Error ? error.message : String(error);
            console.error(`❌ [chatbotStore] Stream error (attempt ${attempt}/2):`, errorMsg);
            console.log(`🔍 [chatbotStore] Is retryable: ${isRetryableError(error)}`);
            
            // Only retry once, and only for network-related errors
            const hasStreamedContent = Boolean(streamingMessageId) || streamedText.length > 0;
            if (attempt === 1 && isRetryableError(error) && !hasStreamedContent) {
              // Log for debugging but don't alarm user
              console.warn(`🔄 [chatbotStore] Retrying after network error...`);
              
              // Show friendly, non-alarming toast (wrapped in try-catch to prevent cascading errors)
              try {
                toast.info('Reconnecting...', {
                  autoClose: 2000,
                  hideProgressBar: true,
                  closeOnClick: true,
                  pauseOnHover: false,
                });
              } catch (toastError) {
                console.warn('Toast notification failed:', toastError);
              }
              
              // Clear current response state first to stop any ongoing renders
              // This helps prevent React error 185 (max update depth) during retry
              set({ currentResponse: null });
              
              // Small delay to let React settle before removing message
              await new Promise(resolve => setTimeout(resolve, 50));
              
              // Remove partial message from UI if one was added
              if (streamingMessageId) {
                const { messages } = get();
                // Use a stable filter to avoid triggering unnecessary re-renders
                const filteredMessages = messages.filter(m => m.id !== streamingMessageId);
                if (filteredMessages.length !== messages.length) {
                  set({ messages: filteredMessages });
                }
              }
              
              // Reset streaming state for retry
              streamedText = '';
              streamingMessageId = null;
              activeToolsSet.clear();
              toolsUsedInMessage.length = 0;
              toolMarkers.length = 0;
              
              // Brief delay before retry to let React fully settle
              await new Promise(resolve => setTimeout(resolve, 1000));
              continue;
            } else if (isRetryableError(error) && hasStreamedContent) {
              console.log('🧭 [chatbotStore] Network error after streaming started - deferring to SSE reconnect');
              throw error;
            }
            
            // Not retryable or second attempt failed
            // Transform to user-friendly message (hide technical details)
            const friendlyError = new Error(
              'Having trouble connecting. Please try again in a moment.'
            );
            throw friendlyError;
          }
          }
        };

        await traceChatTurn(
          'chat.send_message',
          {
            conversationId: activeConversationId,
            isStreaming: true,
            isRetry: isRetry,
          },
          executeStreamWithRetry,
        );

        // Safety check: if somehow we exited the loop without a response, throw
        if (!response) {
          throw lastError || new Error('Failed to get response');
        }

        // POST-STREAM GUARD: If the conversation changed while we were
        // streaming (workspace switch, new chat, etc.), discard the result
        // entirely. The worker will still save the response to S3 so the
        // conversation is available in history when the user returns.
        if (isStaleStream()) {
          console.log('🛑 [chatbotStore] Conversation changed during stream, discarding results');
          set({ currentResponse: null, responseStage: ResponseStage.IDLE });
          return;
        }

        // Flush any pending throttled message update
        if (throttleState.pendingUpdate) {
          throttleState.pendingUpdate();
          throttleState.pendingUpdate = null;
        }

        // Record first-conversation checklist event early — before any early-return
        // paths (questionnaire, continuation) that would skip the main completion block.
        if (isNewConversation) {
          const earlyConversationId = response.conversationId || activeConversationId;
          posthog.capture(EVENTS.CONVERSATION.STARTED, { conversation_id: earlyConversationId });

          // Persist the conversationId now so it's available for subsequent messages
          setConversationId(earlyConversationId);

          // Update the React Query chat history cache so the header title updates immediately
          const nowEpochSeconds = Math.floor(Date.now() / 1000);
          const historyItem: ConversationHistoryItem = {
            conversation_id: earlyConversationId,
            title: deriveConversationTitle(get().messages),
            timestamp: nowEpochSeconds,
            last_activity_at: nowEpochSeconds,
          };
          queryClient.setQueryData<ConversationHistoryItem[]>(
            chatHistoryQueryKeys.list(),
            (old) => {
              const filtered = old?.filter(c => c.conversation_id !== earlyConversationId) || [];
              return [historyItem, ...filtered];
            },
          );
        }

        // Check if this is a structured questionnaire response
        // The agent paused to collect structured input — render the questionnaire UI
        if (response.metadata?.requires_questionnaire && response.metadata?.questionnaire_data) {
          console.log('📋 [chatbotStore] Questionnaire response detected — rendering questionnaire UI');
          const questionnaireData = response.metadata.questionnaire_data;

          // Create or update the assistant message with questionnaire content
          const structuredContent: import('./types').StructuredContent = {
            response: typeof response.content === 'string' ? response.content : '',
            requires_questionnaire: true,
            questionnaire_data: questionnaireData,
          };

          if (streamingMessageId) {
            const { messages: msgs } = get();
            const msgIndex = msgs.findIndex(m => m.id === streamingMessageId);
            if (msgIndex !== -1) {
              const updated = [...msgs];
              updated[msgIndex] = {
                ...updated[msgIndex],
                content: structuredContent,
              };
              set({ messages: updated });
            }
          } else {
            addMessage({
              type: MessageType.ASSISTANT,
              content: structuredContent,
              status: MessageStatus.SENT,
            });
          }

          // Set response to idle — the questionnaire wizard will handle next steps
          get().setActiveTools([]);
          setResponseStage(ResponseStage.IDLE, 'Waiting for questionnaire');
          setTimeout(() => {
            set({ currentResponse: null, responseStage: ResponseStage.IDLE });
          }, 100);
          return;
        }

        // Check if this is a continuation response - if so, skip message handling
        // The conversation was already loaded by handleContinuationComplete and
        // the retry message will be sent separately
        if (response.metadata?.isContinuation) {
          console.log('📂 [chatbotStore] Continuation response detected - skipping message add');
          // The conversation and messages were already loaded by handleContinuationComplete
          // Just clear the state and let the retry message flow handle the rest
          get().setActiveTools([]);
          setResponseStage(ResponseStage.IDLE, 'Complete');
          setTimeout(() => {
            set({ currentResponse: null, responseStage: ResponseStage.IDLE });
          }, 100);
          return; // Early return - don't add any message
        }

        // Parse suggested responses from final content (in case they weren't parsed during streaming)
        const { content: finalCleanedContent, suggestions: finalSuggestions } = parseSuggestedResponses(
          typeof response.content === 'string' ? response.content : ''
        );

        // Update final message with tools used, markers, and suggested responses
        if (streamingMessageId) {
          const { messages } = get();
          const messageIndex = messages.findIndex(m => m.id === streamingMessageId);
          if (messageIndex !== -1) {
            const updated = [...messages];
            updated[messageIndex] = {
              ...updated[messageIndex],
              content: finalCleanedContent || response.content,
              toolsUsed: toolsUsedInMessage.length > 0 ? toolsUsedInMessage : undefined,
              toolMarkers: toolMarkers.length > 0 ? toolMarkers : undefined,
              suggestedResponses: finalSuggestions.length > 0 ? finalSuggestions : (response.suggestedResponses || updated[messageIndex].suggestedResponses || undefined),
            };
            set({ messages: updated });
          }
        } else {
          addMessage({
            type: MessageType.ASSISTANT,
            content: finalCleanedContent || response.content,
            status: MessageStatus.SENT,
            toolsUsed: toolsUsedInMessage.length > 0 ? toolsUsedInMessage : undefined,
            toolMarkers: toolMarkers.length > 0 ? toolMarkers : undefined,
            suggestedResponses: finalSuggestions.length > 0 ? finalSuggestions : (response.suggestedResponses || undefined),
          });
        }

        // Persist conversationId if the backend returned a different one than we generated.
        // (New-conversation persistence is handled in the early block above.)
        const finalConversationId = response.conversationId || activeConversationId;
        if (!isNewConversation && response.conversationId && response.conversationId !== activeConversationId) {
          setConversationId(finalConversationId);
        }

        setChatContext({ conversationId: finalConversationId });
        recordStreamComplete(chatSpanRef);
        addChatBreadcrumb('message_complete', {
          conversationId: finalConversationId,
          toolsUsed: toolsUsedInMessage,
        });

        // Clear active tools and response stage
        get().setActiveTools([]);
        setResponseStage(ResponseStage.IDLE, 'Complete');
        setTimeout(() => {
          set({ currentResponse: null, responseStage: ResponseStage.IDLE });
        }, 100);
      } catch (error) {
        // Log technical details for debugging, but don't show to user
        console.error('Real API response error:', error);

        captureChatError(error, {
          conversationId: activeConversationId,
          isRetry,
        });

        // POST-STREAM GUARD (error path): same as above — if the
        // conversation changed, silently discard the error.
        if (isStaleStream()) {
          console.log('🛑 [chatbotStore] Conversation changed during stream (error path), discarding');
          set({ currentResponse: null, responseStage: ResponseStage.IDLE });
          return;
        }

        // If stream was interrupted, the streamInterrupted UI is already handling it
        // Don't show the general error banner - just clean up state
        const currentStreamInterrupted = get().streamInterrupted;
        if (currentStreamInterrupted.isInterrupted) {
          console.log('🔄 [chatbotStore] Stream interrupted - letting streamInterrupted UI handle it');
          setTimeout(() => {
            set({ currentResponse: null, responseStage: ResponseStage.IDLE });
          }, 100);
          return;
        }

        // If this looks like a brief network disconnect, defer to SSE reconnect flow
        if (isRetryableError(error)) {
          console.log('🔌 [chatbotStore] Network error detected - deferring to reconnect flow');
          get().handleSSEDisconnect();
          return;
        }

        // Check for specific error codes from backend
        const errorCode = (error as any)?.code;
        const isSubscriptionError = errorCode === 'SUBSCRIPTION_REQUIRED';

        // If subscription error, dispatch event to show subscription modal
        if (isSubscriptionError) {
          console.log('🔔 [chatbotStore] Subscription required - dispatching event to show modal');
          window.dispatchEvent(new CustomEvent('subscription-required'));

          // Don't show error in chat - the modal will handle it
          setResponseStage(ResponseStage.IDLE, '');
          setTimeout(() => {
            set({ currentResponse: null, responseStage: ResponseStage.IDLE });
          }, 100);
          return;
        }

        // For other errors, use the calm stream interrupted UI
        const rawMessage = error instanceof Error ? error.message : '';
        const userFriendlyMessage = rawMessage || 'Having trouble connecting. Please try again in a moment.';

        get().handleStreamInterrupted(
          '',
          userFriendlyMessage,
          true,
          content,
          uploadedImages
        );
      }
    },

      setHistorySearchText: (text: string) => {
        set({ historySearchText: text });
      },

      loadConversationById: async (conversationId: string) => {
        // Capture ended event for the conversation being navigated away from
        const previousConversationId = get().conversationId;
        if (previousConversationId && previousConversationId !== conversationId) {
          _captureConversationEnded(previousConversationId);
        }
        console.log('📂 [chatbotStore] loadConversationById called:', conversationId);
        setChatContext({ conversationId });
        addChatBreadcrumb('load_conversation', { conversationId });
        set({
          conversationId,
          conversationLoading: true,
          messages: [], // Clear messages immediately to show skeleton
          showWelcomeScreen: false, // Ensure ChatbotMessages is rendered (not ChatbotWelcome)
          streamInterrupted: initialStreamInterruptedState, // Clear any interrupted state from previous conversation
          sseConnection: initialSSEConnectionState, // Reset SSE connection state
          error: null, // Clear any error state
          executionTodo: null,
        });
        // Initialize tracking for the loaded conversation
        _conversationStartedAt = Date.now();
        _conversationMessageCount = 0;
        try {
          const conversation = await NewConversationService.getById(conversationId);

          // Guard: user navigated away (new chat, different conversation) during fetch
          if (get().conversationId !== conversationId) {
            console.log('📂 [chatbotStore] Discarding stale conversation load:', conversationId);
            return;
          }
          console.log('📂 [chatbotStore] Raw conversation loaded:', {
            conversationId,
            messageCount: conversation.messages?.length || 0,
            metadata: conversation.metadata,
            firstMessagePreview: conversation.messages?.[0]?.message?.slice(0, 100) || 'N/A',
          });

          // Transform the conversation data to match our message format
          const messages: ChatbotMessage[] = conversation.messages?.filter((msg: any) => {
            // Filter out metadata messages - prioritize direct message field
            const content = msg.message ||
              (typeof msg.content === 'string' ? msg.content : msg.content?.response || msg.content?.message || msg.content?.text) ||
              msg.answer || msg.text || '';
            const isMetadata = content === 'START' ||
              content === 'END' ||
              content.startsWith('Conversation titled:') ||
              (msg.role === 'system' && !content.trim()) || // Only filter empty system messages
              (msg.role !== 'tool' && (!content || content.trim() === '')); // Allow empty content for tools

            // Debug logging for continuation conversations
            if (conversationId.includes('cont_') && msg.role === 'assistant') {
              console.log('📂 [chatbotStore] Assistant message in continuation:', {
                role: msg.role,
                contentPreview: content.slice(0, 100),
                isMetadata,
                willFilter: isMetadata,
              });
            }

            return !isMetadata;
          }).map((msg: any) => {

            // Handle different content structures - prioritize direct message field
            let content: string | any;

            if (msg.message) {
              // Direct message field (most common in new API)
              content = msg.message;
            } else if (typeof msg.content === 'string') {
              content = msg.content;
            } else if (msg.content && typeof msg.content === 'object') {
              // If content is an object, check for common properties
              if (msg.content.response) {
                content = msg.content; // Keep structured content for approval messages
              } else if (msg.content.message) {
                content = msg.content.message;
              } else if (msg.content.answer) {
                content = msg.content.answer; // Handle new API format
              } else if (msg.content.text) {
                content = msg.content.text;
              } else {
                // Fallback: stringify the object or use a default
                content = JSON.stringify(msg.content);
              }
            } else {
              // Try other direct properties
              content = msg.answer || msg.text || 'Unknown message content';
            }

            // Determine message type based on role field
            const messageType = msg.role === 'tool' ? MessageType.TOOL : (msg.role === 'user' ? MessageType.USER : MessageType.ASSISTANT);

            // Process images for user messages
            let images: any[] = [];
            if (messageType === MessageType.USER && msg.uploaded_images && Array.isArray(msg.uploaded_images)) {
              images = msg.uploaded_images.map((img: any) => ({
                file: null, // No file object for loaded images
                name: img.name || img.original_name, // Preserve filename for document display
                url: img.url || img.s3_url || img.image_url, // Use presigned URL if available
                uploaded: true,
                s3_url: img.s3_url || img.image_url,
                creative_id: img.creative_id || img.file_id,
                file_id: img.file_id,
                s3_path_to_thumbnail: img.s3_path_to_thumbnail,
                description: img.description, // Include description if available
              }));
            }

            const suggestedResponses = msg.suggested_responses ||
              (msg as any).suggestedResponses ||
              undefined;

            return {
              id: msg.id || generateId(),
              type: messageType,
              content,
              timestamp: msg.timestamp ? new Date(msg.timestamp).getTime() : Date.now(),
              status: MessageStatus.SENT,
              suggestedResponses: suggestedResponses, // Extract suggested responses from agent messages
              images: images.length > 0 ? images : undefined,
              // Map tool specific fields
              tool_name: msg.tool_name,
              tool_args: msg.tool_args,
              tool_output: msg.tool_output,
              is_error: msg.is_error,
              tool_call_id: msg.tool_call_id,
              // Restore hidden flag from backend (for auto-triggered messages)
              hidden: msg.is_hidden || false,
              // Restore display text from backend (e.g., short label for long auto-triggered messages)
              display: msg.display_text || undefined,
            };
          }) || [];

          // Reconstruct toolMarkers on assistant messages from adjacent TOOL messages.
          // During streaming, toolMarkers are built in real-time from SSE events.
          // For loaded conversations, we synthesize them from the TOOL messages in the history
          // so that the same collapsed "N tools used" UI appears on reload.
          //
          // Backend message order: [thought(optional)] → [tools] → [agent response]
          // The thought message is often absent (LLM went straight to tools), so tools
          // typically precede the assistant response. We look BACKWARD from each assistant
          // message to collect the tool block that belongs to it.
          for (let i = 0; i < messages.length; i++) {
            if (messages[i].type !== MessageType.ASSISTANT) continue;
            const assistantMsg = messages[i];

            // Collect consecutive TOOL messages immediately before this assistant message
            let j = i - 1;
            while (j >= 0 && messages[j].type === MessageType.TOOL) {
              j--;
            }
            // j now points to the last non-TOOL message before the tool block (or -1)
            const toolStartIndex = j + 1;
            if (toolStartIndex >= i) continue; // No tools before this assistant message

            const toolMarkers: Array<{ position: number; tool: string; message?: string; success?: boolean | null }> = [];
            for (let k = toolStartIndex; k < i; k++) {
              const toolMsg = messages[k];
              toolMarkers.push({
                position: 0, // All at start — renderContentWithTools groups same-position markers
                tool: toolMsg.tool_name || 'System Tool',
                message: toolMsg.tool_name ? `using ${toolMsg.tool_name}` : undefined,
                success: toolMsg.is_error ? false : true,
              });
              toolMsg.hidden = true; // Hide standalone TOOL card — now shown in the group
            }

            if (toolMarkers.length > 0) {
              assistantMsg.toolMarkers = toolMarkers;
            }
          }

          // Read capacity from S3 metadata (backend is source of truth)
          // Old conversations without metadata.capacity will show 0% until first message exchange
          const metadataCapacity = conversation.metadata?.capacity;
          const capacity = metadataCapacity ? {
            tokenCount: metadataCapacity.token_count || 0,
            capacityPercentage: metadataCapacity.capacity_percentage || 0,
            maxTokens: metadataCapacity.max_tokens || 200000,
            isAtLimit: (metadataCapacity.capacity_percentage || 0) >= 100
          } : {
            tokenCount: 0,
            capacityPercentage: 0,
            maxTokens: 200000,
            isAtLimit: false
          };

          const executionTodo = normalizeExecutionTodo(conversation.metadata?.execution_todo);

          console.log('📂 [chatbotStore] Messages after transformation:', {
            conversationId,
            finalMessageCount: messages.length,
            messageTypes: messages.map(m => m.type),
            firstMessagePreview: messages[0]?.content?.toString().slice(0, 100) || 'N/A',
          });

          set({
            messages,
            conversationId,
            showWelcomeScreen: messages.length === 0 ? true : false, // Only show welcome if no messages
            currentView: ChatbotView.CHAT,
            messageCapacity: capacity,
            executionTodo,
            conversationLoading: false,
          });

          // Store conversationId in the chatbot store (persisted per workspace via Zustand persist)
          get().setConversationId(conversationId);
        } catch (error) {
          console.error('❌ [chatbotStore] Failed to load conversation:', error);

          // Guard: user navigated away during fetch — don't overwrite new state with error
          if (get().conversationId !== conversationId) {
            console.log('📂 [chatbotStore] Discarding stale conversation load error:', conversationId);
            return;
          }

          // Check if this is a "not found" error (conversation was deleted or doesn't exist)
          const errorMessage = error instanceof Error ? error.message : String(error);
          const isNotFoundError = errorMessage.toLowerCase().includes('not found') ||
                                  errorMessage.includes('404');
          
          if (isNotFoundError) {
            // Conversation no longer exists - clear state and show welcome screen
            // Setting conversationId to undefined is automatically persisted via Zustand (workspace-aware)
            console.log('📂 [chatbotStore] Conversation not found, resetting to welcome screen');
            set({
              conversationId: undefined,
              showWelcomeScreen: true,
              conversationLoading: false,
              messages: [],
              executionTodo: null,
            });
          } else {
            // Other errors (network, server, etc.) - show error state
            get().setError({
              id: generateId(),
              code: 'CONVERSATION_LOAD_ERROR',
              message: 'Failed to load conversation',
              timestamp: Date.now(),
              retryable: true,
            });
            set({ conversationLoading: false });
          }
        }
      },

      // Approval actions
      approveTask: async (messageId: string) => {
        const { messages, conversationId, addMessage, updateMessage, setError } = get();

        // Find the message with approval data
        const messageIndex = messages.findIndex(m => m.id === messageId);
        if (messageIndex === -1) {
          throw new Error('Message not found');
        }

        const message = messages[messageIndex];
        const content = message.content;

        if (typeof content === 'string' || !content.requires_approval) {
          throw new Error('Message does not require approval');
        }

        const approvalData = content.approval_data;
        if (!approvalData || !conversationId) {
          throw new Error('Missing approval data or conversation ID');
        }

        try {
          // Build approval request payload
          const pending_tools = Array.isArray(approvalData.pending_tools)
            ? approvalData.pending_tools.map((t: any) => ({
              tool_name: t?.tool_name,
              tool_call_id: t?.tool_call_id,
              approval_text: t?.approval_text,
            }))
            : [];

          // Submit approval
          const response = await chatbotApi.submitApproval(
            conversationId,
            { pending_tools },
            true
          );

          // Mark message as approved
          updateMessage(messageId, {
            isDelegationApproved: true,
          });

          // Add response message if Lambda returns one
          if (response.content && response.content !== 'Task approved successfully.') {
            addMessage({
              type: MessageType.ASSISTANT,
              content: response.content,
              status: MessageStatus.SENT,
            });
          }
        } catch (error) {
          console.error('Approval error:', error);

          setError({
            id: generateId(),
            code: 'APPROVAL_ERROR',
            message: error instanceof Error
              ? error.message
              : 'Failed to approve task. Please try again.',
            timestamp: Date.now(),
            retryable: true,
          });

          throw error;
        }
      },

      cancelApproval: (_messageId: string) => {
        // Optional: Add logic to cancel approval if needed
      },

      // Questionnaire actions
      submitQuestionnaireAnswers: async (messageId: string, answers: import('./types').QuestionnaireAnswer[]) => {
        const { messages, conversationId, updateMessage, setError, setResponseStage, addMessage } = get();

        const messageIndex = messages.findIndex(m => m.id === messageId);
        if (messageIndex === -1) {
          throw new Error('Message not found');
        }

        const message = messages[messageIndex];
        const content = message.content;
        if (typeof content === 'string' || !content.requires_questionnaire) {
          throw new Error('Message does not have a questionnaire');
        }

        if (!conversationId) {
          throw new Error('No active conversation');
        }

        try {
          // Mark questionnaire as completed in the message
          updateMessage(messageId, {
            content: {
              ...content,
              questionnaire_completed: true,
              questionnaire_answers: answers,
            },
          });

          // Show loading state
          setResponseStage(ResponseStage.WAITING, 'Leo is processing your answers...');

          // Stream the agent's continuation response
          let streamingMsgId: string | null = null;
          const response = await chatbotApi.submitQuestionnaireAnswersStream(
            conversationId,
            answers,
            (progress) => {
              // Update response progress (tool markers, streaming content)
              set({ currentResponse: progress });

              // Keep responseStage in sync so the spinner stays visible.
              // We set responseStage directly instead of calling setResponseStage(),
              // which would overwrite currentResponse and wipe streamedContent.
              if (progress.stage === ResponseStage.STREAMING || progress.stage === ResponseStage.TOOL_CALLING) {
                if (get().responseStage !== progress.stage) {
                  set({ responseStage: progress.stage });
                }
              }

              // Create or update streaming message as content arrives
              if (progress.streamedContent && progress.stage === ResponseStage.STREAMING) {
                if (!streamingMsgId) {
                  streamingMsgId = generateId();
                  addMessage({
                    type: MessageType.ASSISTANT,
                    content: progress.streamedContent,
                    status: MessageStatus.SENT,
                    toolMarkers: progress.toolMarkers,
                  });
                  const { messages: msgs } = get();
                  const lastMsg = msgs[msgs.length - 1];
                  if (lastMsg) streamingMsgId = lastMsg.id;
                } else {
                  const { messages: msgs } = get();
                  const idx = msgs.findIndex(m => m.id === streamingMsgId);
                  if (idx !== -1) {
                    const updated = [...msgs];
                    updated[idx] = {
                      ...updated[idx],
                      content: progress.streamedContent,
                      toolMarkers: progress.toolMarkers?.length ? progress.toolMarkers : updated[idx].toolMarkers,
                    };
                    set({ messages: updated });
                  }
                }
              }
            },
          );

          // Check if the resume triggered another questionnaire (chained interrupts,
          // e.g., rundown: transition questionnaire → resume → nudge questionnaire).
          if (response.metadata?.requires_questionnaire && response.metadata?.questionnaire_data) {
            console.log('📋 [chatbotStore] Chained questionnaire detected after resume');
            const questionnaireData = response.metadata.questionnaire_data;

            const structuredContent: import('./types').StructuredContent = {
              response: typeof response.content === 'string' ? response.content : '',
              requires_questionnaire: true,
              questionnaire_data: questionnaireData,
            };

            if (streamingMsgId) {
              const { messages: msgs } = get();
              const idx = msgs.findIndex(m => m.id === streamingMsgId);
              if (idx !== -1) {
                const updated = [...msgs];
                updated[idx] = { ...updated[idx], content: structuredContent };
                set({ messages: updated });
              }
            } else {
              addMessage({
                type: MessageType.ASSISTANT,
                content: structuredContent,
                status: MessageStatus.SENT,
              });
            }

            get().setActiveTools([]);
            setResponseStage(ResponseStage.IDLE, 'Waiting for questionnaire');
            setTimeout(() => {
              set({ currentResponse: null, responseStage: ResponseStage.IDLE });
            }, 100);
            return;
          }

          // Final update with complete content
          if (response.content) {
            if (streamingMsgId) {
              const { messages: msgs } = get();
              const idx = msgs.findIndex(m => m.id === streamingMsgId);
              if (idx !== -1) {
                const updated = [...msgs];
                updated[idx] = { ...updated[idx], content: response.content };
                set({ messages: updated });
              }
            } else {
              addMessage({
                type: MessageType.ASSISTANT,
                content: response.content,
                status: MessageStatus.SENT,
              });
            }
          }

          setResponseStage(ResponseStage.IDLE, 'Complete');
          setTimeout(() => {
            set({ currentResponse: null, responseStage: ResponseStage.IDLE });
          }, 100);
        } catch (error) {
          console.error('Questionnaire submission error:', error);
          setResponseStage(ResponseStage.IDLE);

          setError({
            id: generateId(),
            code: 'QUESTIONNAIRE_ERROR',
            message: error instanceof Error
              ? error.message
              : 'Failed to submit questionnaire. Please try again.',
            timestamp: Date.now(),
            retryable: true,
          });

          throw error;
        }
      },

      // Side modal actions
      openAssetHubModal: () => {
        set({ isAssetHubModalOpen: true });
      },

      closeAssetHubModal: () => {
        set({ isAssetHubModalOpen: false });
      },

      // Continuation actions for context overflow recovery
      handleOverflowRecoveryStarted: (originalConversationId: string) => {
        console.log('🔄 [chatbotStore] Overflow recovery started for conversation:', originalConversationId);

        // Check if we're already in recovery state to prevent duplicate processing
        const currentState = get().continuation;
        if (currentState.isRecovering && currentState.originalConversationId === originalConversationId) {
          console.log('⚠️ [chatbotStore] Already in recovery state for this conversation, skipping duplicate');
          return;
        }

        set({
          continuation: {
            isRecovering: true,
            originalConversationId,
            retryMessage: null,
            recoveryStartTime: Date.now(),
          },
          responseStage: ResponseStage.WAITING,
          currentResponse: {
            requestId: `recovery_${Date.now()}`,
            stage: ResponseStage.WAITING,
            progress: 50,
            startTime: Date.now(),
            estimatedDuration: 40000, // ~40 seconds for summary generation
            currentActivity: 'Creating a summary of our conversation...',
            canCancel: false,
          },
        });

        // Show single, positive toast for expectation management
        // This is the only toast for the entire overflow recovery flow
        // Keep it visible until recovery completes (dismissed in handleContinuationComplete)
        console.log('🔔 [chatbotStore] Showing overflow-recovery toast');
        toast.success('📝 We\'re summarizing this conversation and moving you to a new chat. Please hold on for about 30-40 seconds.', {
          autoClose: false, // Don't auto-close - we'll dismiss it when complete
          toastId: 'overflow-recovery', // Single toast ID for entire flow
        });
      },

      handleContinuationComplete: async (
        newConversationId: string,
        retryMessage: string,
        continuedFrom: string,
        _continuationChain: string[]
      ) => {
        console.log('✅ [chatbotStore] Continuation complete:', {
          newConversationId,
          retryMessage: retryMessage.slice(0, 100) + (retryMessage.length > 100 ? '...' : ''),
          continuedFrom,
        });

        const { loadConversationById, sendMessage, setConversationId } = get();

        try {
          // 1. Update conversation ID to the new continuation
          setConversationId(newConversationId);

          // 2. Load the new conversation (which contains the summary as first message)
          console.log('📂 [chatbotStore] Loading new conversation:', newConversationId);
          await loadConversationById(newConversationId);
          console.log('📂 [chatbotStore] New conversation loaded, messages:', get().messages.length);

          // 3. Clear the continuation state
          set({
            continuation: initialContinuationState,
            responseStage: ResponseStage.IDLE,
            currentResponse: null,
          });

          // 4. Dismiss the recovery toast now that we're done
          toast.dismiss('overflow-recovery');
          console.log('✅ [chatbotStore] Continuation complete - conversation loaded');

          // 5. Invalidate React Query cache and force refetch
          // Using refetchType: 'all' ensures refetch happens even if history panel isn't visible
          console.log('📋 [chatbotStore] Invalidating and refetching chat history cache');
          await queryClient.invalidateQueries({
            queryKey: chatHistoryQueryKeys.list(),
            refetchType: 'all',  // Force refetch even for inactive queries
          });
          // Also update Zustand store for any components that use it directly
          get().loadConversationHistory();

          // 6. Auto-retry the failed message after a brief delay
          // The retry is just a normal agent call - no special timeout needed
          setTimeout(async () => {
            console.log('🔄 [chatbotStore] Auto-retrying message in new conversation');

            try {
              await sendMessage(retryMessage);
              console.log('✅ [chatbotStore] Retry message sent successfully');
            } catch (retryError) {
              console.error('❌ [chatbotStore] Failed to retry message:', retryError);

              // Reset loading state and show error
              set({
                responseStage: ResponseStage.IDLE,
                currentResponse: null,
                error: {
                  id: `retry_error_${Date.now()}`,
                  code: 'RETRY_ERROR',
                  message: retryError instanceof Error
                    ? retryError.message
                    : 'Failed to retry message. Please try sending your message again.',
                  timestamp: Date.now(),
                  retryable: true,
                  originalRequest: retryMessage,
                },
              });

              toast.error('Failed to retry your message. Please try again.', {
                autoClose: 5000,
                toastId: 'retry-error',
              });
            }
          }, 500);
        } catch (error) {
          console.error('❌ [chatbotStore] Failed to complete continuation:', error);
          get().handleOverflowError('Failed to load the continuation conversation. Please try refreshing.');
        }
      },

      handleOverflowError: (errorMessage: string) => {
        console.error('❌ [chatbotStore] Overflow recovery failed:', errorMessage);

        // Dismiss the recovery toast before showing error
        toast.dismiss('overflow-recovery');

        set({
          continuation: initialContinuationState,
          responseStage: ResponseStage.ERROR,
          currentResponse: null,
          error: {
            id: `overflow_error_${Date.now()}`,
            code: 'OVERFLOW_UNRECOVERABLE',
            message: errorMessage,
            timestamp: Date.now(),
            retryable: false,
          },
        });

        toast.error(errorMessage, {
          autoClose: 10000,
          toastId: 'overflow-error',
        });
      },

      clearContinuationState: () => {
        set({
          continuation: initialContinuationState,
        });
      },

      // Stream interrupted actions for retry/resume
      handleStreamInterrupted: (
        partialResponse: string,
        error: string,
        isRetryable: boolean,
        originalMessage: string,
        uploadedImages?: UploadedImage[]
      ) => {
        console.log('❌ [chatbotStore] Stream interrupted:', {
          partialResponseLength: partialResponse.length,
          error,
          isRetryable,
          originalMessageLength: originalMessage.length,
        });

        captureChatWarning('Stream interrupted', {
          error,
          isRetryable,
          partialResponseLength: partialResponse.length,
        });

        const { conversationId } = get();

        clearInterruptionTimers();
        const nextInterruptedState: StreamInterruptedState = {
          isInterrupted: true,
          partialResponse,
          error,
          isRetryable,
          originalMessage,
          uploadedImages: normalizeUploadedImages(uploadedImages),
          interruptedAt: Date.now(),
          conversationId: conversationId || null,
          source: 'llm',
          phase: isRetryable ? 'retry' : 'blocked',
          disconnectStartedAt: null,
          lastReconnectAttemptAt: null,
        };
        set({
          streamInterrupted: {
            ...nextInterruptedState,
          },
          // Keep responseStage at IDLE so the input remains functional
          // The gray retry banner is controlled by streamInterrupted.isInterrupted
          responseStage: ResponseStage.IDLE,
          currentResponse: null,
          // Clear any existing error state - the streamInterrupted UI handles this case
          error: null,
        });
        assertInterruptionState(nextInterruptedState, 'handleStreamInterrupted');

        // Show a calm, non-alarming toast (8 seconds visibility)
        toast.info("Leo encountered an error. You can retry below.", {
          autoClose: 8000,
          toastId: 'stream-interrupted',
        });
      },

      retryInterruptedMessage: async () => {
        const { streamInterrupted, conversationId, setResponseStage } = get();

        if (!streamInterrupted.isInterrupted || !streamInterrupted.originalMessage) {
          console.warn('❌ [chatbotStore] No interrupted message to retry');
          return;
        }

        console.log('🔄 [chatbotStore] Retrying interrupted message');
        addChatBreadcrumb('retry_interrupted', { conversationId, source: streamInterrupted.source });

        // Dismiss the toast
        toast.dismiss('stream-interrupted');

        // Set loading state immediately (like sendMessage does)
        setResponseStage(ResponseStage.WAITING, 'Leo is thinking...');

        // Clear the stream interrupted state and SSE connection state (reset retry counters)
        set({
          streamInterrupted: initialStreamInterruptedState,
          sseConnection: initialSSEConnectionState,
          error: null,
        });

        // Remove the partial assistant message if it exists
        const { messages } = get();
        const lastMessage = messages[messages.length - 1];
        if (lastMessage && lastMessage.type === MessageType.ASSISTANT) {
          // Remove the partial response message
          set({
            messages: messages.slice(0, -1),
          });
        }

        // Retry the message with isRetry=true to avoid duplicate user message
        try {
          await get()._handleRealResponse(
            streamInterrupted.originalMessage,
            generateId(),
            conversationId,
            streamInterrupted.uploadedImages,
            true // isRetry
          );
        } catch (error) {
          console.error('❌ [chatbotStore] Retry failed:', error);
        }
      },

      resumeStream: async () => {
        const { streamInterrupted, sseConnection, conversationId, currentResponse } = get();

        if (!conversationId) {
          console.warn('❌ [chatbotStore] No conversation ID for resume');
          // Fall back to retry
          await get().retryInterruptedMessage();
          return;
        }

        console.log('🔄 [chatbotStore] Attempting to resume stream from event:', sseConnection.lastEventId);

        // Dismiss toasts
        toast.dismiss('stream-interrupted');
        toast.dismiss('connection-lost');

        const lastAssistantContent = (() => {
          const { messages } = get();
          const lastAssistant = [...messages].reverse().find(m => m.type === MessageType.ASSISTANT);
          return typeof lastAssistant?.content === 'string' ? lastAssistant.content : '';
        })();
        const latestKnownPartial = [streamInterrupted.partialResponse, currentResponse?.streamedContent, lastAssistantContent]
          .filter((value): value is string => typeof value === 'string' && value.length > 0)
          .reduce((longest, value) => (value.length > longest.length ? value : longest), '');
        const resumePartial = latestKnownPartial;
        const resumeMessage = streamInterrupted.originalMessage || '';
        const resumeConversationId = conversationId;
        const resumeImages = streamInterrupted.uploadedImages;
        let resumeLastContent = resumePartial;

        const upsertResumeMessage = (
          content: string,
          toolMarkers?: Array<{ position: number; tool: string; runId?: string; message?: string; success?: boolean | null }>
        ) => {
          const { messages } = get();
          const lastAssistantIndex = [...messages].reverse().findIndex(m => m.type === MessageType.ASSISTANT);
          if (lastAssistantIndex !== -1) {
            const targetIndex = messages.length - 1 - lastAssistantIndex;
            const updated = [...messages];
            const existingContent = updated[targetIndex]?.content;
            if (typeof existingContent === 'string' && existingContent.length > content.length) {
              return;
            }
            updated[targetIndex] = {
              ...updated[targetIndex],
              content,
              toolMarkers: toolMarkers || updated[targetIndex].toolMarkers,
            };
            set({ messages: updated });
          } else {
            get().addMessage({
              type: MessageType.ASSISTANT,
              content,
              status: MessageStatus.SENT,
              toolMarkers,
            });
          }
        };

        // Hide the banner while resuming, but keep state locally for fallback
        set({
          streamInterrupted: initialStreamInterruptedState,
        });

        // Set loading state
        set({
          responseStage: ResponseStage.WAITING,
          currentResponse: {
            requestId: `resume_${Date.now()}`,
            stage: ResponseStage.WAITING,
            progress: 50,
            startTime: Date.now(),
            estimatedDuration: 10000,
            currentActivity: 'Reconnecting...',
            canCancel: true,
            streamedContent: resumePartial || undefined,
          },
        });

        const attemptResume = async (attempt: number) => {
          console.log(`🔄 [chatbotStore] Resume attempt ${attempt} from event:`, sseConnection.lastEventId);

          // Try to reconnect to the SSE stream
          // The backend supports resuming via Last-Event-ID header
          const resumedResponse = await chatbotApi.sendMessageStream(
            resumeMessage || '', // Use empty string if no original message (pure resume)
            resumeConversationId,
            resumeImages,
            true, // isRetry
            sseConnection.lastEventId || undefined,
            resumePartial,
            undefined, // isHidden
            (progress: ResponseProgress) => {
              if (progress.streamedContent && progress.streamedContent !== resumeLastContent) {
                resumeLastContent = progress.streamedContent;
                upsertResumeMessage(progress.streamedContent, progress.toolMarkers);
              }
              get().updateResponseProgress(progress);
            }
          );

          if (resumedResponse?.content && typeof resumedResponse.content === 'string') {
            upsertResumeMessage(resumedResponse.content);
          }

          // If successful, clear the interrupted state
          clearInterruptionTimers();
          set({
            streamInterrupted: initialStreamInterruptedState,
            sseConnection: {
              ...get().sseConnection,
              isConnected: true,
              disconnectedAt: null,
              reconnectAttempts: 0,
            },
            responseStage: ResponseStage.IDLE,
            currentResponse: null,
          });

          console.log('✅ [chatbotStore] Resume successful');
        };

        let resumeSucceeded = false;
        let lastResumeError: unknown = null;

        const resumeRetryDelayMs = 1000;

        for (let attempt = 1; attempt <= 3; attempt++) {
          try {
            await attemptResume(attempt);
            resumeSucceeded = true;
            break;
          } catch (error) {
            lastResumeError = error;
            console.error(`❌ [chatbotStore] Resume attempt ${attempt} failed:`, error);
            if (attempt < 3) {
              await new Promise(resolve => setTimeout(resolve, resumeRetryDelayMs));
            }
          }
        }

        if (resumeSucceeded) {
          return;
        }

        console.error('❌ [chatbotStore] Resume failed after retries, falling back to retry:', lastResumeError);

        // Resume failed - fall back to retry behavior
        toast.info('Could not resume. Restarting from your last message...', {
          autoClose: 3000,
          toastId: 'resume-fallback',
        });

        set({
          streamInterrupted: {
            ...initialStreamInterruptedState,
            isInterrupted: true,
            partialResponse: resumePartial,
            error: 'Resume failed. Retrying your message.',
            isRetryable: true,
            originalMessage: resumeMessage,
            uploadedImages: resumeImages || [],
            conversationId: resumeConversationId || null,
            source: 'network',
            phase: 'retry',
            interruptedAt: Date.now(),
          },
        });

        await get().retryInterruptedMessage();
      },

      clearStreamInterruptedState: () => {
        toast.dismiss('stream-interrupted');
        clearInterruptionTimers();
        set({
          streamInterrupted: initialStreamInterruptedState,
        });
      },

      // SSE connection tracking
      updateSSEConnectionState: (state: Partial<SSEConnectionState>) => {
        if (state.lastEventId !== undefined) {
          console.log('🧭 [chatbotStore] SSE lastEventId updated', { lastEventId: state.lastEventId });
        }
        set((prev) => ({
          sseConnection: {
            ...prev.sseConnection,
            ...state,
          },
        }));
      },

      updateStreamInterruptedState: (state: Partial<StreamInterruptedState>) => {
        set((prev) => ({
          streamInterrupted: {
            ...prev.streamInterrupted,
            ...state,
          },
        }));
        const next = { ...get().streamInterrupted, ...state };
        assertInterruptionState(next, 'updateStreamInterruptedState');
      },

      handleSSEDisconnect: () => {
        const { sseConnection, responseStage, messages, conversationId } = get();
        const currentInterrupted = get().streamInterrupted;

        // Only track disconnect if we were actively streaming
        if (responseStage !== ResponseStage.STREAMING && responseStage !== ResponseStage.WAITING) {
          return;
        }

        console.log('⚠️ [chatbotStore] SSE connection lost');

        // Find the last user message - this is what we'd retry
        const lastUserMessage = [...messages].reverse().find(m => m.type === MessageType.USER);
        const originalMessage = typeof lastUserMessage?.content === 'string'
          ? lastUserMessage.content
          : '';
        const originalUploadedImages = normalizeUploadedImages(lastUserMessage?.images);

        // Find partial response (last assistant message if streaming)
        const lastAssistantMessage = [...messages].reverse().find(m => m.type === MessageType.ASSISTANT);
        const partialResponse = responseStage === ResponseStage.STREAMING && lastAssistantMessage
          ? (typeof lastAssistantMessage.content === 'string' ? lastAssistantMessage.content : '')
          : '';

        const disconnectedAt = Date.now();

        clearInterruptionTimers();
        set({
          sseConnection: {
            ...sseConnection,
            isConnected: false,
            disconnectedAt,
            reconnectAttempts: sseConnection.reconnectAttempts + 1,
          },
        });

        if (currentInterrupted.source === 'llm' && currentInterrupted.isInterrupted) {
          console.log('🧭 [chatbotStore] Skipping network interruption due to LLM interruption', {
            phase: currentInterrupted.phase,
            error: currentInterrupted.error,
          });
          return;
        }

        const nextInterruptedState: StreamInterruptedState = {
          isInterrupted: false,
          partialResponse,
          error: 'Connection lost - reconnecting',
          isRetryable: true,
          originalMessage,
          uploadedImages: originalUploadedImages,
          interruptedAt: null,
          conversationId: conversationId || null,
          source: 'network',
          phase: 'silent',
          disconnectStartedAt: disconnectedAt,
          lastReconnectAttemptAt: disconnectedAt,
        };
        set({
          streamInterrupted: {
            ...nextInterruptedState,
          },
        });
        assertInterruptionState(nextInterruptedState, 'handleSSEDisconnect');
        scheduleNetworkPhaseUpdate(get, set, disconnectedAt);
      },

      handleSSEReconnect: () => {
        console.log('✅ [chatbotStore] SSE connection restored');

        toast.dismiss('connection-lost');
        clearInterruptionTimers();

        set({
          sseConnection: {
            ...get().sseConnection,
            isConnected: true,
            disconnectedAt: null,
            reconnectAttempts: 0,
          },
          streamInterrupted: initialStreamInterruptedState,
        });
      },
    })),
    {
      name: STORE_NAME,
      storage: chatbotStoreStorage,
      partialize: (state) => ({
        state: state.state,
        conversationId: state.conversationId,
      }),
      onRehydrateStorage: () => (state) => {
        // Normalize transient states after hydration
        if (state) {
          const normalizedState = normalizeChatbotState(state.state);
          if (normalizedState !== state.state) {
            state.state = normalizedState;
          }
          // Update derived state based on persisted conversationId (per workspace)
          if (state.conversationId) {
            state.showWelcomeScreen = false;
            state.conversationLoading = true;
          } else {
            state.showWelcomeScreen = true;
            state.conversationLoading = false;
          }
        }
      },
    },
  ),
);

// Helper functions
function getProgressForStage(stage: ResponseStage): number {
  switch (stage) {
    case ResponseStage.IDLE: return 100;
    case ResponseStage.WAITING: return 25;
    case ResponseStage.STREAMING: return 75;
    case ResponseStage.ERROR: return 0;
    default: return 0;
  }
}

function getDefaultActivityForStage(stage: ResponseStage): string {
  switch (stage) {
    case ResponseStage.IDLE: return '';
    case ResponseStage.WAITING: return 'Leo is thinking...';
    case ResponseStage.STREAMING: return 'Streaming response...';
    case ResponseStage.ERROR: return 'Error occurred';
    default: return '';
  }
}

function getMockResponse(userMessage: string): string | any {
  const message = userMessage.toLowerCase();

  // Simulate approval response for delegation keywords
  if (message.includes('campaign') || message.includes('delegate') || message.includes('create') || message.includes('launch')) {
    return {
      requires_approval: true,
      response: "I need your approval to execute the following action(s):",
      approval_data: {
        company_name: "mock_company",
        conversation_id: "mock_conv_" + Date.now(),
        type: "write_tool_approval",
        pending_tools: [
          {
            tool_name: "delegate_task_to_experts",
            tool_call_id: "mock_fc_" + Date.now(),
            approval_text: "Create and launch a new Meta lead generation campaign. Budget: $3,000/month (~$100/day). Objective: Lead Generation with Meta lead forms. Structure: 1 CBO campaign with 2 ad sets - Cold Prospecting (interest targeting: Aviation, Airline pilot, Flight school) and Warm Retargeting (30-day website visitors). Targeting: Age 21-45, English, United States & Canada. Creative: Use existing high-performing assets plus create one new static image. Bid Strategy: Lowest Cost with bid cap $1.50 CPL. Success metrics: CPL ≤ $30, 12–15 leads weekly.",
            tool_args: {
              client_requirements_note: "Proceed with full build and launch as approved."
            }
          }
        ]
      }
    };
  }

  if (message.includes('performance') || message.includes('analytics')) {
    return "Here's your current performance overview:\n\n📊 **Key Metrics (Last 30 Days):**\n• Impressions: 125,000 (+15%)\n• Clicks: 3,400 (+22%)\n• CTR: 2.72% ↗️\n• CPC: $1.85 ↘️\n• Conversions: 89 (+31%)\n• ROAS: 4.2x ↗️\n\n🎯 **Top Opportunities:**\n• Mobile campaign optimization\n• Audience expansion potential\n• Creative refresh recommended\n\nWould you like detailed recommendations for any specific area?";
  }

  if (message.includes('competitor') || message.includes('analysis')) {
    return "**Competitive Analysis Summary:**\n\n🔍 **Market Position:**\nYou're currently performing well against key competitors in your space.\n\n📈 **Key Insights:**\n• Your CPC is 23% below industry average\n• Creative engagement rates are above median\n• Opportunity in video advertising (competitors heavy there)\n\n💡 **Recommendations:**\n• Consider expanding to YouTube advertising\n• Test competitor keyword targeting\n• Develop unique value proposition messaging\n\nShall I create a detailed competitive strategy?";
  }

  // Default response
  return "Thanks for your question! I'm here to help with your marketing campaigns, performance analysis, and strategic insights. I can assist with:\n\n• Campaign creation and optimization\n• Performance analysis and reporting\n• Competitive research\n• Audience targeting strategies\n• Creative recommendations\n\nWhat specific area would you like to explore?";
}

// Conversation ID is loaded directly in initialState

// Selectors for optimized subscriptions
// eslint-disable-next-line react-hooks/rules-of-hooks -- These are hook factories accessed via useChatbotSelectors.x(), always called from React components.
export const useChatbotSelectors = {
  messageCapacity: () => useChatbotStore(state => state.messageCapacity),
  state: () => useChatbotStore(state => state.state),
  responseStage: () => useChatbotStore(state => state.responseStage),
  currentView: () => useChatbotStore(state => state.currentView),
  messages: () => useChatbotStore(state => state.messages),
  conversationId: () => useChatbotStore(state => state.conversationId),
  currentResponse: () => useChatbotStore(state => state.currentResponse),
  error: () => useChatbotStore(state => state.error),
  settings: () => useChatbotStore(state => state.settings),
  inputValue: () => useChatbotStore(state => state.inputValue),
  inputFocusRequestId: () => useChatbotStore(state => state.inputFocusRequestId),
  inputFocused: () => useChatbotStore(state => state.inputFocused),
  inputPlaceholderOverride: () => useChatbotStore(state => state.inputPlaceholderOverride),
  showWelcomeScreen: () => useChatbotStore(state => state.showWelcomeScreen),
  conversationHistory: () => useChatbotStore(state => state.conversationHistory),
  historyLoading: () => useChatbotStore(state => state.historyLoading),
  historySearchText: () => useChatbotStore(state => state.historySearchText),
  conversationLoading: () => useChatbotStore(state => state.conversationLoading),
  executionTodo: () => useChatbotStore(state => state.executionTodo),
  isAssetHubModalOpen: () => useChatbotStore(state => state.isAssetHubModalOpen),
  continuation: () => useChatbotStore(state => state.continuation),
  streamInterrupted: () => useChatbotStore(state => state.streamInterrupted),
  interruptAction: () => useChatbotStore(state => deriveInterruptAction(state.streamInterrupted)),
  sseConnection: () => useChatbotStore(state => state.sseConnection),
};

// Capture conversation_ended when the user leaves the page
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    _captureConversationEnded(useChatbotStore.getState().conversationId);
  });
}